/**
 * How a key is exposed in a Sentry context snapshot:
 * - `value`: primitive sent as is
 * - `count`: array length / object key count
 * - `presence`: only whether it is set (secrets, PII)
 */
export type KeyClass = 'value' | 'count' | 'presence';

type Primitive = string | number | boolean | null | undefined;

/** Sentry-safe view of one key; `null` = unset (kept explicit, JSON drops `undefined`). */
export type ValueSummary =
  | { value: string | number | boolean | null }
  | { count: number | null }
  | { set: boolean };

const isPrimitive = (value: unknown): value is Primitive =>
  value === null || (typeof value !== 'object' && typeof value !== 'function');

const isSet = (value: unknown) =>
  value !== undefined && value !== null && value !== '';

const countOf = (value: unknown): number | null => {
  if (Array.isArray(value)) return value.length;
  if (value !== null && typeof value === 'object')
    return Object.keys(value).length;
  return null;
};

/** Summarises `value` by `keyClass`; non-primitive `value`-class values fall back to presence. */
function summarizeValue(keyClass: KeyClass, value: unknown): ValueSummary {
  if (keyClass === 'value' && isPrimitive(value))
    return { value: value ?? null };
  if (keyClass === 'count') return { count: countOf(value) };
  return { set: isSet(value) };
}

/**
 * Summary of every `keyClasses` key of `source` (allowlist: unlisted keys are never read);
 * one failing getter only drops its key.
 */
export function summarizeByKeyClass<K extends string>(
  source: Partial<Record<K, unknown>>,
  keyClasses: Record<K, KeyClass>,
): Partial<Record<K, ValueSummary>> {
  const summary: Partial<Record<K, ValueSummary>> = {};
  (Object.keys(keyClasses) as K[]).forEach((key) => {
    try {
      summary[key] = summarizeValue(keyClasses[key], source[key]);
    } catch (e) {
      // skip unreadable key
    }
  });
  return summary;
}
